import React, {useEffect, useState} from "react";
import './Product.scss'
import {useDispatch, useSelector} from "react-redux";
import {SEARCH_PRODUCT_REQUEST_DATA} from "../../store/product/actions/search-product-actions";
import AutoComplete from "../../components/AutoComplete";
import GenericTable from "../../components/GenericTable";
import {useTranslation} from "react-i18next";
import NewProductModal from "../../components/NewProductModal";
import {resetCreateProductState} from "../../store/product/actions/create-product-actions";
import {toast} from "react-toastify";
import {deleteProductRequest, RESET_DELETE_PRODUCT_STATE} from "../../store/product/actions/delete-product-actions";
import {updateProductRequest, RESET_UPDATE_PRODUCT_STATE} from "../../store/product/actions/update-product-actions";


export const ProductTypes = {
    VIDEO: 'VIDEO', // Assuming possible enum values, replace with actual ones
    AUDIO: 'AUDIO',
    LIGHTING: 'LIGHTING',
    UNKNOWN: 'UNKNOWN'
    // Add other types as necessary
};

export const SupportedClients = {
    DEFAULT: 'DEFAULT',
    SONY: 'SONY',
    ARRI: 'ARRI',
    RED: 'RED',
    // Add other supported clients as necessary
};

const Product = () => {

    const {t} = useTranslation();

    const dispatch = useDispatch();

    const pageSize = 10;

    const {data, isLoading, error} = useSelector(state => state.searchProductsData);

    const {isDeleted} = useSelector(state => state.deleteProduct);

    const {isUpdated} = useSelector(state => state.updateProduct);

    const {response: createResponse} = useSelector(state => state.createProduct);

    const [searchTerm, setSearchTerm] = useState('');

    const [isModalOpen, setModalOpen] = useState(false);

    const [currentPage, setCurrentPage] = useState(0);


    const prioritySearchTableFields = ['name', 'code'];

    const handlePreviousPage = () => {
        if (currentPage > 0) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < data.totalPages - 1) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handleModal = () => {
        dispatch(resetCreateProductState())
        setModalOpen(!isModalOpen)
    }

    const handleProductEdit = (data) => {
        const {id, ...productData} = data;
        dispatch(updateProductRequest(id, productData))
    }

    const customFieldRenderers = {
        type: (value, onChange) => (
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="border px-2 py-1 rounded w-full"
            >
                <option value={ProductTypes.UNKNOWN}>{t('product.default')}</option>
                <option value={ProductTypes.VIDEO}>{t('product.video')}</option>
                <option value={ProductTypes.AUDIO}>{t('product.audio')}</option>
                <option value={ProductTypes.LIGHTING}>{t('product.lighting')}</option>
            </select>
        )
    };

    const customDisplayRenderers = {
        type: (value) => {
            const typeTranslations = {
                [ProductTypes.UNKNOWN]: t('product.default'),
                [ProductTypes.VIDEO]: t('product.video'),
                [ProductTypes.AUDIO]: t('product.audio'),
                [ProductTypes.LIGHTING]: t('product.lighting')
            };
            return typeTranslations[value] || value;
        }
    };

    function handleOnChange(value) {
        setSearchTerm(value);
        if (value.length < 3 && value.length > 0) {
            return
        }
        dispatch({
            type: SEARCH_PRODUCT_REQUEST_DATA,
            payload: {
                params: {
                    searchTerm: value,
                    page: currentPage,
                    size: pageSize
                }
            }
        })
    }


    useEffect(() => {
        dispatch({
            type: SEARCH_PRODUCT_REQUEST_DATA, payload: {
                params: {
                    searchTerm: searchTerm,
                    page: currentPage,
                    size: pageSize
                }
            }
        })
    }, [currentPage])


    useEffect(() => {
        if (isDeleted) {
            toast.success('Product deleted successfully')
            dispatch({
                type: SEARCH_PRODUCT_REQUEST_DATA, payload: {
                    params: {
                        searchTerm: searchTerm,
                        page: currentPage,
                        size: pageSize
                    }
                }
            })


            // Reset after small delay or immediately
            setTimeout(() => {
                dispatch({ type: RESET_DELETE_PRODUCT_STATE });
            }, 300); // enough to allow render updates
        }
    }, [isDeleted]);

    useEffect(() => {
        if (isUpdated) {
            toast.success('Product updated successfully')
            dispatch({
                type: SEARCH_PRODUCT_REQUEST_DATA, payload: {
                    params: {
                        searchTerm: searchTerm,
                        page: currentPage,
                        size: pageSize
                    }
                }
            })

            // Reset after small delay
            setTimeout(() => {
                dispatch({ type: RESET_UPDATE_PRODUCT_STATE });
            }, 300);
        }
    }, [isUpdated]);

    useEffect(() => {
        if (createResponse) {
            toast.success('Product created successfully')
            setModalOpen(false);
            dispatch({
                type: SEARCH_PRODUCT_REQUEST_DATA, payload: {
                    params: {
                        searchTerm: searchTerm,
                        page: currentPage,
                        size: pageSize
                    }
                }
            })

            // Reset after small delay
            setTimeout(() => {
                dispatch(resetCreateProductState());
            }, 300);
        }
    }, [createResponse]);

    return (
        <div className="p-10 container mx-auto customers-container">
            <br/>
            <div className="flex gap-10">
                <div className="inline-flex items-center">
                    <button onClick={handleModal}
                            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                            style={{backgroundColor: '#34495e'}}>
                        {t('product.addProduct')}
                    </button>
                </div>
            </div>
            <br/>
            <AutoComplete enableSuggestions={false} onChange={handleOnChange}/>
            <br/>
            {
                data.value ?
                    <GenericTable
                        data={data.value}
                        columnsToIgnore={['id']}
                        priorityColumns={prioritySearchTableFields}
                        onEdit={handleProductEdit}
                        onDelete={(row) => dispatch(deleteProductRequest(row.id))}
                        translationKeyPrefix={'product'}
                        nonEditableFields={['code']}
                        customFieldRenderers={customFieldRenderers}
                        customDisplayRenderers={customDisplayRenderers}
                    />
                    : null
            }
            <br/>
            {
                data.totalPages > 1 ?
                    <div className="flex justify-between items-center mt-4">
                        <button onClick={handlePreviousPage} disabled={currentPage <= 0}
                                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                                style={{backgroundColor: '#34495e'}}>
                            {t('pagination.previous')}
                        </button>
                        <span>{t('pagination.page')} {currentPage + 1} {t('pagination.of')} {data.totalPages}</span>
                        <button onClick={handleNextPage} disabled={currentPage >= data.totalPages - 1}
                                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                                style={{backgroundColor: '#34495e'}}>
                            {t('pagination.next')}
                        </button>
                    </div> : null
            }
            <br/>
            <br/>
            <NewProductModal isOpen={isModalOpen} onClose={handleModal}/>
        </div>
    )
}

export default Product;
